// animate-entity.ts — animateEntityChanges is the unified cinematic diff/patch
// for an entity's visible state: HP / mana / temp HP / AP / reaction / status
// effects / dead state / name. Delegates to animateStatusEffectChanges at the end.

import type { EntityRow } from './types';
import { encounterCreatureField, computedValue, layerOf, posCoord } from '../shared/array-types';
import { syncCreatureTokenThumbMediaIfChanged } from './media-signature';
import { animateStatusEffectChanges } from './animate-status-effects';
import { syncSwarmGhosts } from './swarm-ghosts';

type CreatureRenderWindow = Window & {
    syncInventoryTokenThumbMediaIfChanged?: (el: HTMLElement, entity: EntityRow) => boolean;
};

/**
 * The geometry-host slice this file needs to re-size + re-anchor a token after a media/aspect
 * change. Duck-typed on purpose: media-signature/animate-entity live in the creature-render bundle,
 * which stays independent of encounter-map — the caster_update / encounter_update callers
 * callers pass the owning presentation host explicitly.
 */
interface FT3DGeometryHost {
    applyTokenZoomSize(el: HTMLElement): void;
    getTokenPixelWidth(el: HTMLElement): number;
    getTokenPixelHeight(el: HTMLElement): number;
    pixelFromGridX(x: number): number;
    pixelFromGridZ(z: number): number;
    applyEncounterCeilingClip(el: HTMLElement, z: number, height: number): void;
}

function isFT3DGeometryHost(w: unknown): w is FT3DGeometryHost {
    return !!w
        && typeof (w as FT3DGeometryHost).applyTokenZoomSize === 'function'
        && typeof (w as FT3DGeometryHost).getTokenPixelHeight === 'function'
        && typeof (w as FT3DGeometryHost).pixelFromGridZ === 'function';
}

/**
 * Unified cinematic animation system — handles EVERY visual change for an entity
 * HP bars, mana, temp HP, flashes, status effects, dead state, AND position movement
 * NEW: Temporarily forces the bar overlay visible when HP/mana changes so the animation is visible
 * ADDED: Blue mana glow pulse on the whole creature token when mana changes noticeably
 * FIXED: Lowered mana threshold to catch small spends (e.g., -1 mana) reliably
 */
function animateEntityChanges(
    oldRef: HTMLElement | Record<string, unknown> | null | undefined,
    newEl: HTMLElement,
    entity: EntityRow,
    widget: unknown = null,
): void {

    // Unified name + tooltip update for map tokens (creatures + inventory)
    const creatureLike = entity as { encounter_creatures?: { encounter_creature_id?: ReadonlyArray<string> } };
    const inventoryLike = entity as {
        inventory_id?: ReadonlyArray<string>;
        inventory_name?: ReadonlyArray<string>;
        item_name?: ReadonlyArray<string>;
        item_qty?: ReadonlyArray<string | number>;
        name?: ReadonlyArray<string>;
        item_arr?: { name?: ReadonlyArray<string> };
    };
    const isCreature = !!(encounterCreatureField(creatureLike.encounter_creatures, 'encounter_creature_id'));
    const isInventory = !!(ftInventoryField(inventoryLike, 'inventory_id'));

    let newName = '';
    if (isCreature) {
        newName = getCreatureName(entity);
    } else if (isInventory) {
        newName = decodeHtmlEntitiesForPlainText(ftInventoryField(inventoryLike, 'inventory_name') ?? '');
    } else {
        // More robust fallback for inventory items
        newName = inventoryLike.item_name?.[0]
               || ftInventoryField(inventoryLike, 'inventory_name')
               || inventoryLike.name?.[0]
               || inventoryLike.item_arr?.name?.[0]
               || 'Item';
    }

    if (newName) {
        const escapedNewName = escapeHtml(newName);
        const currentTitle = newEl.getAttribute('title') || newEl.getAttribute('data-bs-title') || '';

        if (escapedNewName !== currentTitle) {
            // Set BOTH (Bootstrap 5 needs data-bs-title)
            newEl.setAttribute('title', escapedNewName);
            newEl.setAttribute('data-bs-title', escapedNewName);

            // Kill old instance
            const existingTip = bootstrap.Tooltip.getInstance(newEl);
            if (existingTip) ftDisposeBootstrapTooltipInstanceSafe(existingTip);

            // Create fresh + FORCE refresh
            const newTip = new bootstrap.Tooltip(newEl, {
                boundary: 'window',
                trigger: 'hover focus',
                html: true
            });
            newTip.update();   // ← this is what was missing for inventory

            ftDebugLog('ft3d animate', { tooltipNameUpdated: newName });
        }
    }

    if (isCreature) {
        const mediaGeometryChanged = syncCreatureTokenThumbMediaIfChanged(newEl, entity);
        // A media swap can flip the token's ASPECT RATIO (shapeshift werewolf <-> man). The inner-node
        // swap updated the outer data-thumbnail_* dims but NOT the rendered pixel size or the ground
        // anchor — mirror the encounter_update path (websocket/encounter-update.ts) so the token takes
        // the new form's shape and rests on the floor instead of floating. z_pos is server-authoritative
        // (preserve_token_bottom_after_size_change re-grounds the form on whatever it stood on — floor,
        // table, tree), so adopt it from the fresh blob and recompute the pixel top from z + the NEW
        // height; do NOT locally recompute z as height/2. [Codex 2026-07-10]
        if (mediaGeometryChanged && isFT3DGeometryHost(widget)) {
            // Don't fight an in-flight optimistic drag that owns the DOM position (same flag the
            // encounter_update position block honors).
            if (newEl.dataset.xzPosOptimistic === undefined) {
                const nextX = posCoord(entity, 'x_pos');
                const nextZ = posCoord(entity, 'z_pos');
                if (nextX !== undefined && nextX !== null) newEl.dataset.x = String(nextX);
                if (nextZ !== undefined && nextZ !== null) newEl.dataset.z = String(nextZ);
            }
            const gx = parseFloat(newEl.dataset.x || '0');
            const gz = parseFloat(newEl.dataset.z || '0');
            widget.applyTokenZoomSize(newEl);
            const wPx = widget.getTokenPixelWidth(newEl);
            const hPx = widget.getTokenPixelHeight(newEl);
            newEl.style.left = `${widget.pixelFromGridX(gx) - wPx / 2}px`;
            newEl.style.top  = `${widget.pixelFromGridZ(gz) - hPx / 2}px`;
            widget.applyEncounterCeilingClip(newEl, gz, hPx);
        }
    } else if (isInventory) {
        // Inventory analogue (window-attached from the inventory bundle; reached as an ambient global
        // because it lives in a different bundle than this creature-render carve-out). Repaints a floor
        // item's map art when the blob's picture/video no longer matches what's painted — the surgical
        // path that lets update_inventory_pic_id / WS pushes update art without a full XZ widget rebuild.
        (window as CreatureRenderWindow).syncInventoryTokenThumbMediaIfChanged?.(newEl, entity);
    }

    const getOld = (key: string, fallback: number = 0): number => {
        if (!oldRef) return fallback;
        if (oldRef instanceof HTMLElement) {
            return parseFloat(oldRef.dataset[key] ?? String(fallback));
        }
        if (typeof oldRef === 'object') {
            const v = (oldRef as Record<string, unknown>)[key];
            return parseFloat(v != null ? String(v) : String(fallback));
        }
        return fallback;
    };

    // ───────────────────────────────────────────────────
    // 1. Gather old values
    // ───────────────────────────────────────────────────
    const oldHpPct    = getOld('v_bar_v_percent', 0);
    const oldTempPct  = getOld('v_bar_v_temp_percent', 0);
    const oldAp       = getOld('aps', 3);
    let oldManaPct = 0;
    if (isCreature) {
        oldManaPct = getOld('mana_percent', 0);
    }

    // ───────────────────────────────────────────────────
    // 2. New values
    // ───────────────────────────────────────────────────
    // Vitality moved off v_arr to computed.vitality (descalared 2026-05-29) — read via computedValue.
    const newHpPct    = parseFloat(String(computedValue(entity, 'vitality', 'v_bar_v_percent') ?? 0));
    const newTempPct  = parseFloat(String(computedValue(entity, 'vitality', 'v_bar_v_temp_percent') ?? 0));
    const newManaPct  = isCreature
        ? parseFloat(String(encounterCreatureField(creatureLike.encounter_creatures, 'mana_percent') ?? 0))
        : 0;
    const newAp       = isCreature
        ? parseInt(String(encounterCreatureField(creatureLike.encounter_creatures, 'aps') ?? '3'), 10)
        : 0;

    // ───────────────────────────────────────────────────
    // 3. VITALITY BARS
    // ───────────────────────────────────────────────────
    const mainBar = newEl.querySelector<HTMLElement>(
        '.hp-bar .hp-main, .hp-bar .progress-bar.hp-main, .hp-bar .progress-bar, .progress-bar.hp-main'
    );
    let tempBar = newEl.querySelector<HTMLElement>('.hp-bar .hp-temp, .hp-bar .bg-warning');
    // Legacy tokens rendered before `.hp-temp` was always emitted: create the segment
    // so a later temp-V grant can animate on push without a full reload.
    if (!tempBar && isCreature) {
        const prog = newEl.querySelector('.hp-bar .progress');
        if (prog) {
            tempBar = document.createElement('div');
            tempBar.className = 'progress-bar hp-temp';
            tempBar.style.display = 'none';
            tempBar.style.width = '0%';
            prog.appendChild(tempBar);
        }
    }

    if (mainBar) {
        mainBar.style.transition = 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
        void mainBar.offsetWidth;
        mainBar.style.width = `${newHpPct}%`;
    }

    if (tempBar) {
        tempBar.style.transition = 'width 0.7s ease-out 0.1s';
        void tempBar.offsetWidth;
        tempBar.style.width = `${newTempPct}%`;
        tempBar.style.display = newTempPct > 0 ? 'block' : 'none';
    }

    // Flash on damage/heal
    if (Math.abs(oldHpPct - newHpPct) > 0.5) {
        const container = newEl.querySelector('.hp-bar .progress') || newEl.querySelector('.hp-bar');
        if (container) {
            container.classList.add('flash-hp');
            setTimeout(() => container.classList.remove('flash-hp'), 800);
        }
    }

    // Swarm ghosts thin out as V drops; no-op for non-swarm creatures.
    if (isCreature) {
        syncSwarmGhosts(newEl, entity);
    }

    // ───────────────────────────────────────────────────
    // 4. MANA BAR (creatures only)
    // ───────────────────────────────────────────────────
    if (isCreature) {
        const manaBar = newEl.querySelector<HTMLElement>('.mana-bar .progress-bar');
        if (manaBar) {
            manaBar.style.transition = 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
            void manaBar.offsetWidth;
            manaBar.style.width = `${newManaPct}%`;
        }
    }

    // ───────────────────────────────────────────────────
    // 5. COMBAT WIDGET OVERLAYS (existing — unchanged)
    // ───────────────────────────────────────────────────
    if (isCreature) {
        const combatId = encounterCreatureField(creatureLike.encounter_creatures, 'encounter_creature_id');
        const combatEl = combatId ? document.getElementById(`combat_widget_${combatId}`) : null;
        if (combatEl) {
            const damageOverlay = combatEl.querySelector<HTMLElement>('.vitality-damage-overlay');
            const tempOverlay   = combatEl.querySelector<HTMLElement>('.vitality-temp-overlay');
            const lostPercent = 100 - newHpPct;

            if (damageOverlay) {
                damageOverlay.style.transition = 'height 0.8s cubic-bezier(0.16, 1, 0.3, 1)';
                void damageOverlay.offsetHeight;
                damageOverlay.style.height = `${lostPercent}%`;
            }
            if (tempOverlay) {
                tempOverlay.style.transition = 'height 0.7s ease-out 0.1s';
                void tempOverlay.offsetHeight;
                tempOverlay.style.height = `${newTempPct}%`;
                tempOverlay.style.display = newTempPct > 0 ? 'block' : 'none';
            }
        }
    }

    // ───────────────────────────────────────────────────
    // 6. TEMP HP FADE (existing — unchanged)
    // ───────────────────────────────────────────────────
    if (tempBar) {
        if (oldTempPct <= 0 && newTempPct > 0) {
            tempBar.style.opacity = '0';
            requestAnimationFrame(() => {
                tempBar!.style.transition = 'opacity 0.5s ease-out';
                tempBar!.style.opacity = '1';
            });
        } else if (oldTempPct > 0 && newTempPct <= 0) {
            tempBar.style.transition = 'opacity 0.4s ease-in';
            tempBar.style.opacity = '0';
        }
    }

    // ───────────────────────────────────────────────────
    // 7. ACTION POINTS (existing — unchanged)
    // ───────────────────────────────────────────────────
    if (isCreature) {
        const apDots = newEl.querySelector<HTMLElement>('.ap-dots');
        if (apDots) {
            const badge = apDots.querySelector<HTMLElement>('.badge');
            if (badge) {
                badge.textContent = newAp > 0 ? String(newAp) : '';
            }
            const reactionIcon = apDots.querySelector('img');
            if (reactionIcon) {
                const iconName = encounterCreatureField<number>(creatureLike.encounter_creatures, 'reaction') === 1
                    ? 'ap_reaction_dark_mode.png'
                    : 'ap_reaction_used_dark_mode.png';
                reactionIcon.src = `https://cdn.fatetide.com/images/icons/${iconName}`;
            }
            if (oldAp !== newAp) {
                apDots.classList.add('pulse-ap');
                setTimeout(() => apDots.classList.remove('pulse-ap'), 600);
            }
        }
    }

    // ───────────────────────────────────────────────────
    // 8. QUANTITY BADGE (inventory — unchanged)
    // ───────────────────────────────────────────────────
    if (isInventory) {
        const qtyBadge = newEl.querySelector<HTMLElement>('.quantity-badge');
        if (qtyBadge) {
            const newQty = parseInt(String(ftInventoryField(inventoryLike, 'item_qty') ?? 0), 10);
            const oldQty = getOld('inventory_qty', 0);

            if (newQty > 1) {
                qtyBadge.textContent = String(newQty);
                qtyBadge.style.display = 'flex';
            } else {
                qtyBadge.style.display = 'none';
            }

            if (oldQty !== newQty && oldQty > 0 && newQty > 0) {
                qtyBadge.classList.add('pulse-quantity');
                setTimeout(() => qtyBadge.classList.remove('pulse-quantity'), 600);
            }

            newEl.dataset.inventory_qty = String(newQty);
        }
    }

    // ───────────────────────────────────────────────────
    // 9. UPDATE DATASET WITH NEW VALUES (CRITICAL FIX FOR STALE OLD VALUES)
    // ───────────────────────────────────────────────────
    newEl.dataset.v_bar_v_percent = String(newHpPct);
    newEl.dataset.v_bar_v_temp_percent = String(newTempPct);
    if (isCreature) {
        newEl.dataset.mana_percent = String(newManaPct);
        newEl.dataset.aps = String(newAp);
    }

    // ───────────────────────────────────────────────────
    // 10. TEMPORARY OVERLAY REVEAL WHEN BARS CHANGE — VITALITY + MANA ONLY
    // ───────────────────────────────────────────────────
    let vitalityChanged = false;
    let manaChanged = false;

    if (Math.abs(oldHpPct - newHpPct) > 2 || Math.abs(oldTempPct - newTempPct) > 2) {
        vitalityChanged = true;
    }

    // Lowered threshold to catch small mana spends (e.g., -1 mana = ~5% on typical casters)
    // Dataset sync prevents any float noise false-positives
    if (isCreature && Math.abs(oldManaPct - newManaPct) > 1) {
        manaChanged = true;
    }

    const shouldReveal = vitalityChanged || (isCreature && manaChanged);

    if (shouldReveal) {
        const overlayClass = isCreature ? '.encounter-creature-overlay' : '.inventory-item-overlay';
        const overlay = newEl.querySelector<HTMLElement>(overlayClass);

        if (overlay) {
            if (newEl.dataset.overlayTimeoutId) {
                clearTimeout(Number(newEl.dataset.overlayTimeoutId));
                delete newEl.dataset.overlayTimeoutId;
            }

            overlay.style.transition = 'opacity 0.35s ease-out';
            overlay.style.opacity = '1';

            const timeoutId = setTimeout(() => {
                overlay.style.transition = 'opacity 0.4s ease-in';
                overlay.style.opacity = '0';

                setTimeout(() => {
                    overlay.style.opacity = '';
                    overlay.style.transition = '';
                    delete newEl.dataset.overlayTimeoutId;
                }, 450);
            }, 2400);

            newEl.dataset.overlayTimeoutId = String(timeoutId);
        }
    }

    // ───────────────────────────────────────────────────
    // 11. MANA CHANGE ANIMATION — BLUE GLOW PULSE ON THE WHOLE CREATURE TOKEN
    // ───────────────────────────────────────────────────
    if (manaChanged) {
        // Brief blue glow + slight scale pulse to "animate the creature" when mana changes
        newEl.style.transition = 'none';
        newEl.style.boxShadow = '0 0 30px rgba(80, 180, 255, 0.95)';
        newEl.style.transform = 'scale(1.08)';
        void newEl.offsetWidth; // force reflow

        newEl.style.transition = 'box-shadow 0.9s ease-out, transform 0.9s ease-out';
        newEl.style.boxShadow = '0 0 0 rgba(80, 180, 255, 0)';
        newEl.style.transform = 'scale(1)';
    }


    // ───────────────────────────────────────────────────
    // 12. LIGHT CHANGES
    // ───────────────────────────────────────────────────
    if (newEl.dataset.light_just_changed === '1') {
        const oldLightVal = parseFloat(newEl.dataset.bright_light_dim_light || '0');
        // layerOf(arr,name) — NOT layerValue(arr,name), which returns the row's items.item_id UUID
        // (parseFloat→bogus radius). Matches the encounter-update light-dataset reader.
        const newLightVal = parseFloat(String(layerOf(entity, 'bright_light_dim_light') ?? '0'));

        if (oldLightVal === 0 && newLightVal > 0) {
            // Light just turned ON → bright magical flash + glow pulse
            newEl.classList.add('casting-light-flash');
            setTimeout(() => {
                newEl.classList.remove('casting-light-flash');
                newEl.dataset.light_just_changed = '0'; // consume flag
            }, 1200);
        } else if (oldLightVal > 0 && newLightVal === 0) {
            // Light just turned OFF → brief dimming pulse
            newEl.classList.add('light-extinguished');
            setTimeout(() => {
                newEl.classList.remove('light-extinguished');
                newEl.dataset.light_just_changed = '0'; // consume flag
            }, 800);
        } else {
            // Edge case: value changed but not from/to 0 — still consume
            newEl.dataset.light_just_changed = '0';
        }
    }

    // ───────────────────────────────────────────────────
    // 13. STATUS EFFECTS + DEAD STATE
    // ───────────────────────────────────────────────────
    animateStatusEffectChanges(newEl, oldRef as EntityRow | null, entity);
}

Object.assign(window, { animateEntityChanges });

export { animateEntityChanges };
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
